/*
 * Design: Command Center — Dark Operations Dashboard
 * Sticky top nav, DM Sans, minimal, brand gradient accent on CTA
 * Mobile hamburger menu with slide-in panel
 * All CTAs → Demo AI Now modal
 */

import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { Menu, X } from "lucide-react";


const LOGO_URL = "https://files.manuscdn.com/user_upload_by_module/session_file/105433247/qBHQzWZzhhwDPsto.png";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Pulse System", href: "/pulse" },
  { label: "AI Systems", href: "#ai-systems" },
  { label: "Blog", href: "/blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);


  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [, navigate] = useLocation();

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#09090B]/90 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              if (window.location.pathname === "/") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                window.location.href = "/";
              }
            }}
            className="flex items-center gap-3 shrink-0"
          >
            <img src={LOGO_URL} alt="The AI Assistant Lab" className="h-9 w-9 sm:h-10 sm:w-10" />
            <span className="text-white font-semibold text-base sm:text-lg tracking-tight hidden sm:block">
              The AI Assistant Lab
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-zinc-300 hover:text-white text-sm font-medium transition-colors duration-200"
              >
                {link.label}
              </button>
            ))}
            <a
              href="sms:+15039987760?body=Hi%20Luis%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20AI%20systems%20and%20GHL%20services."
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg hover:shadow-purple-500/20"
              style={{
                background: "linear-gradient(135deg, #7C3AED, #D946EF)",
              }}
            >
              Text Me
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-zinc-400 hover:text-white p-2"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-[#09090B]/95 backdrop-blur-xl border-t border-white/5 px-4 py-4 space-y-1">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className="block w-full text-left text-zinc-300 hover:text-white text-base font-medium py-3 px-3 rounded-lg hover:bg-white/5 transition-colors duration-200"
            >
              {link.label}
            </button>
          ))}
          <a
            href="sms:+15039987760?body=Hi%20Luis%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20AI%20systems%20and%20GHL%20services."
            onClick={() => setMobileOpen(false)}
            className="block w-full text-center text-white text-base font-semibold py-3 px-3 rounded-lg mt-2 transition-all duration-200"
            style={{
              background: "linear-gradient(135deg, #7C3AED, #D946EF)",
            }}
          >
            Text Me
          </a>
        </div>
      </div>
    </nav>
  );
}
